/**
 * Similes in a text: "like" and "as … as" comparisons, with the false positives filtered
 * out and listed.
 *
 * A simile compares two unlike things with a marker word. That makes it findable by
 * pattern, and it also makes the pattern wrong a lot: "I like tea" and "as soon as
 * possible" contain the marker words and are not similes. This finder works from the
 * words around the marker, with no grammar parser, so it is a filter, not a verdict. It
 * says which of its calls are firm (`likely`) and which are guesses (`possible`), and it
 * lists what it set aside and why, so the reader can overrule it.
 *
 * Metaphors have no marker, so a pattern cannot find them; see the tool's page.
 */

export type SimileKind = "as-as" | "like" | "adjective-as";
export type Confidence = "likely" | "possible";

export type FilterReason =
  | "idiom"
  | "degree"
  | "verb"
  | "filler"
  | "pointing"
  | "example";

export type Note = "perception" | "approximation";

export interface SimileHit {
  kind: SimileKind;
  confidence: Confidence;
  start: number;
  end: number;
  text: string;
  /** What the comparison is to: the words after "like" or the second "as". */
  vehicle: string;
  note: Note | null;
}

export interface FilteredHit {
  start: number;
  end: number;
  text: string;
  reason: FilterReason;
}

export interface SimileResult {
  hits: SimileHit[];
  filtered: FilteredHit[];
}

interface Token {
  word: string;
  text: string;
  start: number;
  end: number;
  /** The text between the previous token and this one. */
  before: string;
}

const RE_WORD = /\p{L}+(?:['’]\p{L}+)*/gu;
const RE_APOSTROPHE = /’/g;
const RE_SENTENCE_BREAK = /[.!?;:—\n]/;
const RE_ANY_BREAK = /[.!?;:—,\n()]/;
const MAX_VEHICLE_WORDS = 5;

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_WORD)) {
    const start = match.index ?? 0;
    tokens.push({
      word: match[0].toLowerCase().replace(RE_APOSTROPHE, "'"),
      text: match[0],
      start,
      end: start + match[0].length,
      before: text.slice(last, start),
    });
    last = start + match[0].length;
  }
  return tokens;
}

const SET = (words: string): ReadonlySet<string> => new Set(words.split(" "));

const DETERMINERS = SET(
  "a an the my your his her its our their this that these those one some any no every each"
);
const SUBJECT_PRONOUNS = SET("i he she it they we you one who");
/** Words that, right before "like", make it a verb ("I like", "they like"). */
const LIKE_SUBJECTS = SET("i we they you who");
const IDIOM_MIDDLES = SET("soon long well far much many often early late");
const DEGREE_ENDINGS = SET(
  "possible necessary usual ever before expected planned yet before always"
);
const AUXILIARIES = SET(
  "do does did don't doesn't didn't would wouldn't will won't can can't could couldn't should shouldn't may might must shall to really also just not never always often ever sometimes still both all"
);
const PERCEPTION = SET(
  "look looks looked looking feel feels felt feeling sound sounds sounded seem seems seemed smell smells smelled taste tastes tasted"
);
const APPROXIMATE = SET("something nothing anything everything");
const POINTING = SET("this that so");
const OBJECT_PRONOUNS = SET("me him her us them you it");
const STOPPERS = SET(
  "and but or nor so yet because since although though when while whenever where which who whom whose that if then than as like until before after"
);
const CLAUSE_STARTERS = SUBJECT_PRONOUNS;

/**
 * Adjectives that make a one-"as" simile ("lonely as a cloud", "white as snow"). Without a
 * parser, an "as" after any other word could be a role ("worked as a clerk"), so only
 * these are matched.
 */
const SIMILE_ADJECTIVES = SET(
  "bald blind bold brave bright busy calm clean clear cold cool cunning dead deaf dry dull easy fast fat fierce firm flat fresh gentle good graceful green happy hard harmless heavy high hot hungry innocent keen light lonely loud mad mean mild neat nimble old pale patient plain playful pleased poor pretty proud pure quick quiet red regular rich right safe sharp shy sick silent slow sly smooth snug soft solid sound sour steady stiff still straight strong sure sweet swift tall thick thin tight tired tough warm weak wet white wild wise yellow young"
);

/** True when a gap is a comma that only separates a repeated word ("red, red rose"). */
function repeatsAcrossComma(
  token: Token,
  previous: Token | undefined
): boolean {
  return (
    previous !== undefined &&
    token.before.trim() === "," &&
    token.word === previous.word
  );
}

/** The vehicle phrase after a marker: up to a break, a conjunction or a few words. */
function vehicleFrom(tokens: readonly Token[], from: number): Token[] {
  const phrase: Token[] = [];
  for (
    let i = from;
    i < tokens.length && phrase.length < MAX_VEHICLE_WORDS;
    i += 1
  ) {
    const token = tokens[i];
    const previous = phrase.at(-1);
    if (
      i > from &&
      RE_ANY_BREAK.test(token.before) &&
      !repeatsAcrossComma(token, previous)
    ) {
      break;
    }
    if (
      previous &&
      (STOPPERS.has(token.word) || CLAUSE_STARTERS.has(token.word))
    ) {
      break;
    }
    phrase.push(token);
  }
  return phrase;
}

function spanText(text: string, first: Token, last: Token): string {
  return text.slice(first.start, last.end);
}

/* ------------------------------------------------------------------ as … as */

function asAsAt(
  text: string,
  tokens: readonly Token[],
  i: number
): { hit?: SimileHit; filtered?: FilteredHit; next: number } | null {
  if (tokens[i].word !== "as") {
    return null;
  }
  for (let j = i + 2; j <= i + 4 && j < tokens.length; j += 1) {
    if (
      RE_SENTENCE_BREAK.test(tokens[j].before) ||
      tokens[j].before.includes(",")
    ) {
      return null;
    }
    if (tokens[j].word !== "as") {
      continue;
    }
    const middle = tokens[i + 1].word;
    const vehicle = vehicleFrom(tokens, j + 1);
    const last = vehicle.at(-1) ?? tokens[j];
    const span = {
      start: tokens[i].start,
      end: last.end,
      text: spanText(text, tokens[i], last),
    };
    if (IDIOM_MIDDLES.has(middle) || vehicle.length === 0) {
      return { filtered: { ...span, reason: "idiom" }, next: j };
    }
    const first = vehicle[0].word;
    if (CLAUSE_STARTERS.has(first) || DEGREE_ENDINGS.has(first)) {
      return { filtered: { ...span, reason: "degree" }, next: j };
    }
    return {
      hit: {
        kind: "as-as",
        confidence:
          DETERMINERS.has(first) || vehicle.length <= 2 ? "likely" : "possible",
        ...span,
        vehicle: spanText(text, vehicle[0], last),
        note: null,
      },
      next: j,
    };
  }
  return null;
}

/* ---------------------------------------------------------- adjective + as */

function adjectiveAsAt(
  text: string,
  tokens: readonly Token[],
  i: number
): SimileHit | null {
  const previous = tokens[i - 1];
  const marker = tokens[i];
  const next = tokens[i + 1];
  if (
    marker.word !== "as" ||
    !previous ||
    !next ||
    !SIMILE_ADJECTIVES.has(previous.word) ||
    RE_ANY_BREAK.test(marker.before) ||
    RE_ANY_BREAK.test(next.before) ||
    CLAUSE_STARTERS.has(next.word)
  ) {
    return null;
  }
  const vehicle = vehicleFrom(tokens, i + 1);
  const last = vehicle.at(-1);
  if (!last) {
    return null;
  }
  return {
    kind: "adjective-as",
    confidence: DETERMINERS.has(next.word) ? "likely" : "possible",
    start: previous.start,
    end: last.end,
    text: spanText(text, previous, last),
    vehicle: spanText(text, vehicle[0], last),
    note: null,
  };
}

/* --------------------------------------------------------------------- like */

function reasonToSkipLike(
  tokens: readonly Token[],
  i: number
): FilterReason | null {
  const next = tokens[i + 1];
  const previous = tokens[i - 1];
  if (next?.before.includes(",")) {
    return "filler";
  }
  const atClauseStart = !previous || RE_SENTENCE_BREAK.test(tokens[i].before);
  if (
    !atClauseStart &&
    previous &&
    (AUXILIARIES.has(previous.word) || LIKE_SUBJECTS.has(previous.word))
  ) {
    return "verb";
  }
  if (!next || RE_ANY_BREAK.test(next.before)) {
    return "verb";
  }
  if (previous && PERCEPTION.has(previous.word) && next.word.endsWith("ing")) {
    return "verb";
  }
  if (next.word === "to" || (next.word === "it" && !atClauseStart)) {
    return "verb";
  }
  if (POINTING.has(next.word)) {
    return "pointing";
  }
  if (OBJECT_PRONOUNS.has(next.word) && !atClauseStart) {
    return "example";
  }
  return null;
}

function likeAt(
  text: string,
  tokens: readonly Token[],
  i: number
): { hit?: SimileHit; filtered?: FilteredHit } | null {
  const marker = tokens[i];
  if (marker.word !== "like") {
    return null;
  }
  const vehicle = vehicleFrom(tokens, i + 1);
  const last = vehicle.at(-1) ?? marker;
  const span = {
    start: marker.start,
    end: last.end,
    text: spanText(text, marker, last),
  };
  const reason = reasonToSkipLike(tokens, i);
  if (reason) {
    return { filtered: { ...span, reason } };
  }
  const previous = tokens[i - 1];
  let note: Note | null = null;
  if (previous && PERCEPTION.has(previous.word)) {
    note = "perception";
  } else if (previous && APPROXIMATE.has(previous.word)) {
    note = "approximation";
  }
  const first = vehicle[0]?.word ?? "";
  const confident = DETERMINERS.has(first) && note === null;
  const start =
    note === "perception" && previous ? previous.start : marker.start;
  return {
    hit: {
      kind: "like",
      confidence: confident ? "likely" : "possible",
      start,
      end: span.end,
      text: text.slice(start, span.end),
      vehicle: vehicle.length > 0 ? spanText(text, vehicle[0], last) : "",
      note,
    },
  };
}

/* ------------------------------------------------------------------- finder */

/** Find the similes in a text, in reading order. */
export function findSimiles(text: string): SimileResult {
  const tokens = tokenize(text);
  const hits: SimileHit[] = [];
  const filtered: FilteredHit[] = [];
  let skipUntil = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (i <= skipUntil) {
      continue;
    }
    const asAs = asAsAt(text, tokens, i);
    if (asAs) {
      if (asAs.hit) {
        hits.push(asAs.hit);
      }
      if (asAs.filtered) {
        filtered.push(asAs.filtered);
      }
      skipUntil = asAs.next;
      continue;
    }
    const adjective = adjectiveAsAt(text, tokens, i);
    if (adjective) {
      hits.push(adjective);
      continue;
    }
    const like = likeAt(text, tokens, i);
    if (like?.hit) {
      hits.push(like.hit);
    } else if (like?.filtered) {
      filtered.push(like.filtered);
    }
  }
  return { hits, filtered };
}

/** The sentence around a span, for showing a hit in context. */
export function sentenceAround(
  text: string,
  start: number,
  end: number
): string {
  let from = start;
  while (from > 0 && !".!?\n".includes(text[from - 1])) {
    from -= 1;
  }
  let to = end;
  while (to < text.length && !".!?\n".includes(text[to])) {
    to += 1;
  }
  return text.slice(from, Math.min(to + 1, text.length)).trim();
}
